import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["id", "type", "language", "playlistId", "trackId"];
import { useMemo } from 'react';
import { useCatalogCardModels } from '../../catalog/hooks/useCatalogCardModels';
import { useSimplePlaylists } from '../../hubspotPlaylists/hooks/useSimplePlaylists';
import { useUserProgressForLearningContent } from '../../userProgress/hooks/useUserProgressForLearningContent';
const isEqualItem = (itemA, itemB) => {
  var _itemA$language, _itemB$language;
  return itemA.id === itemB.id && itemA.type === itemB.type && (itemA.type === 'PLAYLIST' || itemA.type === 'LEARNING_PATH' || ('language' in itemA ? ((_itemA$language = itemA.language) === null || _itemA$language === void 0 ? void 0 : _itemA$language.toLowerCase()) === ((_itemB$language = itemB.language) === null || _itemB$language === void 0 ? void 0 : _itemB$language.toLowerCase()) : true));
};
export const useLearningContentCardModels = ({
  items,
  skip,
  skipProgress
}) => {
  const playlistIds = items.filter(({
    type
  }) => type === 'LEARNING_PATH' || type === 'PLAYLIST').map(item => item.id);
  const catalogItems = items.filter(({
    type
  }) => type !== 'LEARNING_PATH' && type !== 'PLAYLIST');

  // hydrated models for catalog items (tracks, lessons, shorts)
  const {
    catalogCardModels,
    catalogCardModelsLoading,
    catalogCardModelsError
  } = useCatalogCardModels({
    items: catalogItems,
    skip
  });

  // hydrated models for playlists
  const {
    simplePlaylists,
    simplePlaylistsLoading,
    simplePlaylistsError
  } = useSimplePlaylists({
    playlistIds,
    skip
  });

  // user progress data for all items
  const {
    userProgressData,
    userProgressDataLoading,
    userProgressDataError
  } = useUserProgressForLearningContent({
    items,
    skip: skipProgress || skip
  });

  // merge all data into a single array of card models
  const cardModels = useMemo(() => {
    if (catalogCardModelsLoading || simplePlaylistsLoading) return null;
    const modelsAndProgress = items.map(item => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const {
          id,
          type
        } = item,
        customProps = _objectWithoutPropertiesLoose(item, _excluded);
      let cardModel;
      if (type === 'LEARNING_PATH' || type === 'PLAYLIST') {
        const playlist = simplePlaylists === null || simplePlaylists === void 0 ? void 0 : simplePlaylists[item.id];
        if (playlist) {
          cardModel = Object.assign({}, playlist, {
            type: playlist.type === 'LEARNING_PATH' ? 'LEARNING_PATH' : 'PLAYLIST'
          });
        }
      } else {
        cardModel = catalogCardModels === null || catalogCardModels === void 0 ? void 0 : catalogCardModels.find(m => isEqualItem(m, item));
      }
      let progress;
      if (cardModel && userProgressData) {
        progress = userProgressData.find(p => isEqualItem(p, item));
        if (!progress) {
          // if there is no progress found it means the user has not started the item
          progress = {
            id,
            type,
            status: 'NOT_STARTED',
            completedPercentage: 0,
            completedLessonIds: []
          };
        }
      } else if (!userProgressDataLoading) progress = null;
      return Object.assign({}, cardModel, {
        progress,
        id: cardModel && item.id
      }, customProps);
    });
    return modelsAndProgress.filter(m => !!m.id);
  }, [catalogCardModels, catalogCardModelsLoading, items, simplePlaylists, simplePlaylistsLoading, userProgressData, userProgressDataLoading]);
  return {
    cardModels,
    cardModelsLoading: catalogCardModelsLoading || simplePlaylistsLoading,
    cardModelsError: catalogCardModelsError || simplePlaylistsError,
    userProgressDataError,
    userProgressDataLoading
  };
};